import { call, put, takeEvery, takeLatest } from 'redux-saga/effects';
import { format } from 'date-fns';
import * as T from './clickHouseConstants';
import * as actions from './clickHouseActions';
import { clickHouseApi, getSnapshotDetails, getLiveItemSalesV2Api, getLiveByChannelApi, getLivePaymentsApi, getLiveRefundDetailsApi, getLiveRefundSummaryApi, getLiveVoidSummaryApi, getLiveVoidDetailsApi, getLiveHourlySalesApi, getLiveStaffPerformanceApi, getLiveStaffActiveClockingApi, getLiveStaffActiveClockingV2Api, getOrderLogApi, getOrderLogExportApi, getOrderDetailApi, getLiveUnpaidOrdersApi, getLiveCompOrderLogApi, getLiveCompReasonsApi, getLiveCompByStaffApi, getLiveCategorySalesApi, getLiveStaffRefundsApi, getSnapshotComparison, getChannelNamesApi, getLiveOutstandingValueApi, getLabourEmployeeSalesApi, getLabourTipsApi, getLabourVoidsApi, getLabourWorkingHoursApi, getLabourStaffPerformanceApi, getLabourWorkingHoursLogApi, getLabourKpiComparisonApi, getHourlySalesApi, getLabourEmployeesApi, getSalesByChannelBreakdownApi } from './clickHouseApi';
import { downloadOrderLogCsv, downloadOrderLogExcel, downloadOrderLogPdf } from '../../../utils/exportUtils';
import { comparisonQueryParams } from '../../../utils/reportPeriod';
import { setServerClock } from '../../../utils/dateTimeUtils';
import type { OrderLogItem } from '../../../interface/clickHouseInterface';

function* fetchClickHouseSaga(
  action: ReturnType<typeof actions.fetchClickHouseRequest>
) {
  try {
    const response: any = yield call(clickHouseApi, action.payload.locationId);
    yield put(actions.fetchClickHouseSuccess(response.data));
  } catch (error: any) {
    yield put(actions.fetchClickHouseFailure(error.message || 'Failed to fetch ClickHouse data'));
  }
}

function* fetchSnapshotDetailsSaga(
  action: ReturnType<typeof actions.fetchSnapshotDetailsRequest>
) {
  try {
    const response: any = yield call(getSnapshotDetails, { locationId: action.payload.locationId, startDate: action.payload.startDate, endDate: action.payload.endDate, orderSource: action.payload.orderSource });
    yield put(actions.fetchSnapshotDetailsSuccess(response));
  } catch (error: any) {
    yield put(actions.fetchSnapshotDetailsFailure(error.message || 'Failed to fetch snapshot details'));
  }
}

function* fetchLiveItemSalesV2Saga(
  action: ReturnType<typeof actions.fetchLiveItemSalesV2Request>
) {
  try {
    const { pinnedItemNames, ...rest } = action.payload;
    const apiPayload = { ...rest, ...(pinnedItemNames?.length ? { pinnedItemNames } : {}) };
    const response: any = yield call(getLiveItemSalesV2Api, apiPayload);
    const content = Array.isArray(response) ? response : (response?.content ?? response?.data ?? []);
    yield put(actions.fetchLiveItemSalesV2Success({
      content,
      totalElements: response?.totalElements,
      totalPages: response?.totalPages,
    }));
  } catch (error: any) {
    yield put(actions.fetchLiveItemSalesV2Failure(error.message || 'Failed to fetch live item sales v2'));
  }
}

function* fetchLiveByChannelSaga(
  action: ReturnType<typeof actions.fetchLiveByChannelRequest>
) {
  try {
    const response: any = yield call(getLiveByChannelApi, action.payload);
    const data = Array.isArray(response) ? response : (response?.data ?? []);
    yield put(actions.fetchLiveByChannelSuccess(data));
  } catch (error: any) {
    yield put(actions.fetchLiveByChannelFailure(error.message || 'Failed to fetch live by channel'));
  }
}

// Third-Party Marketplace Performance card — same by-channel API, but the payload never
// carries orderSource, so the card ignores the global sales-channel filter (SSE-16)
function* fetchThirdPartyByChannelSaga(
  action: ReturnType<typeof actions.fetchThirdPartyByChannelRequest>
) {
  try {
    const response: any = yield call(getLiveByChannelApi, action.payload);
    const data = Array.isArray(response) ? response : (response?.data ?? []);
    yield put(actions.fetchThirdPartyByChannelSuccess(data));
  } catch (error: any) {
    yield put(actions.fetchThirdPartyByChannelFailure(error.message || 'Failed to fetch third-party by channel'));
  }
}

function* fetchLivePaymentsSaga(
  action: ReturnType<typeof actions.fetchLivePaymentsRequest>
) {
  try {
    const response: any = yield call(getLivePaymentsApi, action.payload);
    const data = Array.isArray(response) ? response : (response?.data ?? []);
    yield put(actions.fetchLivePaymentsSuccess(data));
  } catch (error: any) {
    yield put(actions.fetchLivePaymentsFailure(error.message || 'Failed to fetch live payments'));
  }
}

function* fetchLiveRefundSummarySaga(
  action: ReturnType<typeof actions.fetchLiveRefundSummaryRequest>
) {
  try {
    const response: any = yield call(getLiveRefundSummaryApi, action.payload);
    const data = Array.isArray(response) ? response : (response?.data ?? []);
    yield put(actions.fetchLiveRefundSummarySuccess(data));
  } catch (error: any) {
    yield put(actions.fetchLiveRefundSummaryFailure(error.message || 'Failed to fetch live refund summary'));
  }
}

function* fetchLiveRefundDetailsSaga(
  action: ReturnType<typeof actions.fetchLiveRefundDetailsRequest>
) {
  try {
    const response: any = yield call(getLiveRefundDetailsApi, action.payload);
    const content = Array.isArray(response) ? response : (response?.content ?? response?.data ?? []);
    yield put(actions.fetchLiveRefundDetailsSuccess({
      content,
      totalPages: response?.totalPages,
      totalElements: response?.totalElements,
      number: response?.number,
      size: response?.size,
    }));
  } catch (error: any) {
    yield put(actions.fetchLiveRefundDetailsFailure(error.message || 'Failed to fetch live refund details'));
  }
}

function* fetchLiveVoidSummarySaga(
  action: ReturnType<typeof actions.fetchLiveVoidSummaryRequest>
) {
  try {
    const { locationId, startDate, endDate, orderSource } = action.payload;
    const response: any = yield call(getLiveVoidSummaryApi, locationId, undefined, startDate, endDate, orderSource);
    const data = Array.isArray(response) ? response : (response?.data ?? []);
    yield put(actions.fetchLiveVoidSummarySuccess(data));
  } catch (error: any) {
    yield put(actions.fetchLiveVoidSummaryFailure(error.message || 'Failed to fetch void summary'));
  }
}

function* fetchLiveVoidDetailsSaga(
  action: ReturnType<typeof actions.fetchLiveVoidDetailsRequest>
) {
  try {
    const response: any = yield call(getLiveVoidDetailsApi, action.payload);
    const content = Array.isArray(response) ? response : (response?.content ?? response?.data ?? []);
    yield put(actions.fetchLiveVoidDetailsSuccess({
      content,
      totalPages: response?.totalPages,
      totalElements: response?.totalElements,
      number: response?.number,
      size: response?.size,
    }));
  } catch (error: any) {
    yield put(actions.fetchLiveVoidDetailsFailure(error.message || 'Failed to fetch void details'));
  }
}

function* fetchLiveHourlySalesSaga(
  action: ReturnType<typeof actions.fetchLiveHourlySalesRequest>
) {
  try {
    const response: any = yield call(getLiveHourlySalesApi, action.payload.locationId, undefined, action.payload.orderSource);
    // API returns { hours: [...], ... }; also handle legacy { data: [...] } and direct array
    const data = Array.isArray(response) ? response : (response?.hours ?? response?.data ?? []);
    yield put(actions.fetchLiveHourlySalesSuccess(data));
  } catch (error: any) {
    yield put(actions.fetchLiveHourlySalesFailure(error.message || 'Failed to fetch live hourly sales'));
  }
}


function* fetchHourlySalesSaga(
  action: ReturnType<typeof actions.fetchHourlySalesRequest>
) {
  try {
    const { locationId, startDate, endDate, orderSource } = action.payload;
    const today = format(new Date(), 'yyyy-MM-dd');

    if (startDate === today && endDate === today) {
      // The date-range endpoint returns all zeros for the current live day.
      // Use the no-dates live endpoint which returns real-time running totals
      // in the same HourlySalesResponse shape: { hours: [...], operatingStartHour, ... }
      const liveResponse: any = yield call(getLiveHourlySalesApi, locationId, undefined, orderSource);
      const normalized = liveResponse?.hours !== undefined ? liveResponse : (liveResponse?.data ?? liveResponse);
      yield put(actions.fetchHourlySalesSuccess(normalized));
    } else {
      const response: any = yield call(getHourlySalesApi, action.payload);
      const normalized = response?.hours !== undefined ? response : (response?.data ?? response);
      yield put(actions.fetchHourlySalesSuccess(normalized));
    }
  } catch (error: any) {
    yield put(actions.fetchHourlySalesFailure(error.message || 'Failed to fetch hourly sales'));
  }
}

function* fetchLiveStaffPerformanceSaga(
  action: ReturnType<typeof actions.fetchLiveStaffPerformanceRequest>
) {
  try {
    const response: any = yield call(getLiveStaffPerformanceApi, action.payload);
    const data = Array.isArray(response) ? response : (response?.data ?? []);
    yield put(actions.fetchLiveStaffPerformanceSuccess(data));
  } catch (error: any) {
    yield put(actions.fetchLiveStaffPerformanceFailure(error.message || 'Failed to fetch live staff performance'));
  }
}

function* fetchLiveStaffActiveClockingSaga(
  action: ReturnType<typeof actions.fetchLiveStaffActiveClockingRequest>
) {
  try {
    const response: any = yield call(getLiveStaffActiveClockingApi, action.payload);
    const data = Array.isArray(response) ? response : (response?.data ?? []);
    yield put(actions.fetchLiveStaffActiveClockingSuccess(data));
  } catch (error: any) {
    yield put(actions.fetchLiveStaffActiveClockingFailure(error.message || 'Failed to fetch live staff active clocking'));
  }
}

function* fetchLiveStaffActiveClockingV2Saga(
  action: ReturnType<typeof actions.fetchLiveStaffActiveClockingV2Request>
) {
  try {
    const response: any = yield call(getLiveStaffActiveClockingV2Api, action.payload);
    yield put(actions.fetchLiveStaffActiveClockingV2Success(response));
  } catch (error: any) {
    yield put(actions.fetchLiveStaffActiveClockingV2Failure(error.message || 'Failed to fetch staff roster'));
  }
}

function* fetchOrderLogSaga(action: ReturnType<typeof actions.fetchOrderLogRequest>) {
  try {
    const response: any = yield call(getOrderLogApi, action.payload);
    yield put(actions.fetchOrderLogSuccess(response));
  } catch (error: any) {
    yield put(actions.fetchOrderLogFailure(error.message || 'Failed to fetch order log'));
  }
}

function* exportOrderLogSaga(action: ReturnType<typeof actions.exportOrderLogRequest>) {
  try {
    const response: any = yield call(getOrderLogExportApi, action.payload);
    const orders: OrderLogItem[] = response?.orders ?? (Array.isArray(response) ? response : []);
    const { format, filename, currencySymbol } = action.payload;
    if (format === 'csv') {
      yield call(downloadOrderLogCsv, orders, filename, currencySymbol);
    } else if (format === 'excel') {
      yield call(downloadOrderLogExcel, orders, filename, currencySymbol);
    } else {
      yield call(downloadOrderLogPdf, orders, filename, currencySymbol);
    }
    yield put(actions.exportOrderLogSuccess());
  } catch (error: any) {
    yield put(actions.exportOrderLogFailure(error.message || 'Export failed'));
  }
}

function* fetchLiveUnpaidOrdersSaga(
  action: ReturnType<typeof actions.fetchLiveUnpaidOrdersRequest>
) {
  try {
    const response: any = yield call(getLiveUnpaidOrdersApi, { locationId: action.payload.locationId, page: 1, size: action.payload.size ?? 10, startDate: action.payload.startDate, endDate: action.payload.endDate, orderSource: action.payload.orderSource });
    yield put(actions.fetchLiveUnpaidOrdersSuccess(response));
  } catch (error: any) {
    yield put(actions.fetchLiveUnpaidOrdersFailure(error.message || 'Failed to fetch unpaid orders'));
  }
}

function* fetchLiveUnpaidLogSaga(
  action: ReturnType<typeof actions.fetchLiveUnpaidLogRequest>
) {
  try {
    const response: any = yield call(getLiveUnpaidOrdersApi, action.payload);
    yield put(actions.fetchLiveUnpaidLogSuccess(response));
  } catch (error: any) {
    yield put(actions.fetchLiveUnpaidLogFailure(error.message || 'Failed to fetch unpaid order log'));
  }
}

function* fetchSnapshotComparisonSaga(action: ReturnType<typeof actions.fetchSnapshotComparisonRequest>) {
  try {
    // ZAR-1661 — the comparison window is FE-owned: it follows the SHAPE of the selected
    // period (same weekday a week back for a single day, the preceding equal-length
    // window for 7D/30D, the prior calendar month, …), which only the resolver knows.
    // Derived here rather than in the screens so both Snapshot and Sales — and every
    // drill that re-fires this action — send the same window without repeating the logic,
    // and so no component computes a comparison period of its own.
    const { startDate, endDate } = action.payload;
    const comparison: { compStartDate?: string; compEndDate?: string } =
      comparisonQueryParams(startDate, endDate);

    const response: any = yield call(getSnapshotComparison, { ...action.payload, ...comparison });

    // ZAR-1662 — this is the only endpoint that reports the server's own clock
    // (`currentTime` as wall clock in `locationTimezone`), taken as it computed the
    // numbers above. Mirror it so the date control's "(to 7 AM)" truncation label states
    // where the DATA stops instead of where the viewer's device clock stands. A payload
    // without those fields is ignored and the browser clock keeps the label.
    setServerClock(response?.currentTime, response?.locationTimezone);

    yield put(actions.fetchSnapshotComparisonSuccess(response));
  } catch (error: any) {
    yield put(actions.fetchSnapshotComparisonFailure(error.message || 'Failed to fetch snapshot comparison'));
  }
}

function* fetchChannelNamesSaga(action: ReturnType<typeof actions.fetchChannelNamesRequest>) {
  try {
    const response: any = yield call(getChannelNamesApi, action.payload.locationId);
    const data = Array.isArray(response) ? response : (response?.data ?? []);
    yield put(actions.fetchChannelNamesSuccess(data));
  } catch (error: any) {
    yield put(actions.fetchChannelNamesFailure(error.message || 'Failed to fetch channel names'));
  }
}

export default function* clickHouseSaga() {
  yield takeEvery(T.FETCH_CLICKHOUSE_REQUEST, fetchClickHouseSaga);
  yield takeLatest(T.FETCH_SNAPSHOT_DETAILS_REQUEST, fetchSnapshotDetailsSaga);
  yield takeLatest(T.FETCH_SNAPSHOT_COMPARISON_REQUEST, fetchSnapshotComparisonSaga);
  yield takeLatest(T.FETCH_LIVE_ITEM_SALES_V2_REQUEST, fetchLiveItemSalesV2Saga);
  yield takeLatest(T.FETCH_LIVE_BY_CHANNEL_REQUEST, fetchLiveByChannelSaga);
  yield takeLatest(T.FETCH_THIRD_PARTY_BY_CHANNEL_REQUEST, fetchThirdPartyByChannelSaga);
  yield takeLatest(T.FETCH_LIVE_PAYMENTS_REQUEST, fetchLivePaymentsSaga);
  yield takeLatest(T.FETCH_LIVE_REFUND_SUMMARY_REQUEST, fetchLiveRefundSummarySaga);
  yield takeLatest(T.FETCH_LIVE_REFUND_DETAILS_REQUEST, fetchLiveRefundDetailsSaga);
  yield takeLatest(T.FETCH_LIVE_VOID_SUMMARY_REQUEST, fetchLiveVoidSummarySaga);
  yield takeLatest(T.FETCH_LIVE_VOID_DETAILS_REQUEST, fetchLiveVoidDetailsSaga);
  yield takeLatest(T.FETCH_LIVE_HOURLY_SALES_REQUEST, fetchLiveHourlySalesSaga);
  yield takeLatest(T.FETCH_HOURLY_SALES_REQUEST, fetchHourlySalesSaga);
  yield takeLatest(T.FETCH_LIVE_STAFF_PERFORMANCE_REQUEST, fetchLiveStaffPerformanceSaga);
  yield takeLatest(T.FETCH_LIVE_STAFF_ACTIVE_CLOCKING_REQUEST, fetchLiveStaffActiveClockingSaga);
  yield takeLatest(T.FETCH_LIVE_STAFF_ACTIVE_CLOCKING_V2_REQUEST, fetchLiveStaffActiveClockingV2Saga);
  yield takeLatest(T.FETCH_ORDER_LOG_REQUEST, fetchOrderLogSaga);
  yield takeLatest(T.EXPORT_ORDER_LOG_REQUEST, exportOrderLogSaga);
  yield takeLatest(T.FETCH_ORDER_DETAIL_REQUEST, fetchOrderDetailSaga);
  yield takeLatest(T.FETCH_LIVE_UNPAID_ORDERS_REQUEST, fetchLiveUnpaidOrdersSaga);
  yield takeLatest(T.FETCH_LIVE_UNPAID_LOG_REQUEST, fetchLiveUnpaidLogSaga);
  yield takeLatest(T.FETCH_LIVE_COMP_ORDER_LOG_REQUEST, fetchLiveCompOrderLogSaga);
  yield takeLatest(T.FETCH_LIVE_COMP_REASONS_REQUEST, fetchLiveCompReasonsSaga);
  yield takeLatest(T.FETCH_LIVE_COMP_BY_STAFF_REQUEST, fetchLiveCompByStaffSaga);
  yield takeLatest(T.FETCH_LIVE_CATEGORY_SALES_REQUEST, fetchLiveCategorySalesSaga);
  yield takeLatest(T.FETCH_LIVE_STAFF_REFUNDS_REQUEST, fetchLiveStaffRefundsSaga);
  yield takeLatest(T.FETCH_CHANNEL_NAMES_REQUEST, fetchChannelNamesSaga);
  yield takeLatest(T.FETCH_LIVE_OUTSTANDING_VALUE_REQUEST, fetchLiveOutstandingValueSaga);
  yield takeLatest(T.FETCH_LABOUR_EMPLOYEE_SALES_REQUEST, fetchLabourEmployeeSalesSaga);
  yield takeLatest(T.FETCH_LABOUR_TIPS_REQUEST, fetchLabourTipsSaga);
  yield takeLatest(T.FETCH_LABOUR_VOIDS_REQUEST, fetchLabourVoidsSaga);
  yield takeLatest(T.FETCH_LABOUR_WORKING_HOURS_REQUEST, fetchLabourWorkingHoursSaga);
  yield takeLatest(T.FETCH_LABOUR_STAFF_PERFORMANCE_REQUEST, fetchLabourStaffPerformanceSaga);
  yield takeLatest(T.FETCH_LABOUR_WORKING_HOURS_LOG_REQUEST, fetchLabourWorkingHoursLogSaga);
  yield takeLatest(T.FETCH_LABOUR_KPI_COMPARISON_REQUEST, fetchLabourKpiComparisonSaga);
  yield takeLatest(T.FETCH_LABOUR_EMPLOYEES_REQUEST, fetchLabourEmployeesSaga);
  yield takeLatest(T.FETCH_SALES_BY_CHANNEL_BREAKDOWN_REQUEST, fetchSalesByChannelBreakdownSaga);
}

// Sales by Channel — Financial Breakdown (ZAR-1555)
function* fetchSalesByChannelBreakdownSaga(action: ReturnType<typeof actions.fetchSalesByChannelBreakdownRequest>) {
  try {
    const response: any = yield call(getSalesByChannelBreakdownApi, action.payload);
    yield put(actions.fetchSalesByChannelBreakdownSuccess({
      rows: Array.isArray(response?.rows) ? response.rows : [],
      totals: response?.totals ?? null,
    }));
  } catch (error: any) {
    yield put(actions.fetchSalesByChannelBreakdownFailure(error.message || 'Failed to fetch sales by channel breakdown'));
  }
}

function* fetchOrderDetailSaga(
  action: ReturnType<typeof actions.fetchOrderDetailRequest>
) {
  try {
    const response: any = yield call(getOrderDetailApi, action.payload);
    yield put(actions.fetchOrderDetailSuccess(response));
  } catch (error: any) {
    yield put(actions.fetchOrderDetailFailure(error.message || 'Failed to fetch order detail'));
  }
}

function* fetchLiveCompOrderLogSaga(
  action: ReturnType<typeof actions.fetchLiveCompOrderLogRequest>
) {
  try {
    const response: any = yield call(getLiveCompOrderLogApi, action.payload);
    yield put(actions.fetchLiveCompOrderLogSuccess(response));
  } catch (error: any) {
    yield put(actions.fetchLiveCompOrderLogFailure(error.message || 'Failed to fetch comp order log'));
  }
}

function* fetchLiveCompReasonsSaga(
  action: ReturnType<typeof actions.fetchLiveCompReasonsRequest>
) {
  try {
    const response: any = yield call(getLiveCompReasonsApi, action.payload);
    yield put(actions.fetchLiveCompReasonsSuccess(response));
  } catch (error: any) {
    yield put(actions.fetchLiveCompReasonsFailure(error.message || 'Failed to fetch comp reasons'));
  }
}

function* fetchLiveCompByStaffSaga(
  action: ReturnType<typeof actions.fetchLiveCompByStaffRequest>
) {
  try {
    const response: any = yield call(getLiveCompByStaffApi, action.payload);
    const staff = response?.staff ?? (Array.isArray(response) ? response : []);
    yield put(actions.fetchLiveCompByStaffSuccess(staff));
  } catch (error: any) {
    yield put(actions.fetchLiveCompByStaffFailure(error.message || 'Failed to fetch comp by staff'));
  }
}

function* fetchLiveCategorySalesSaga(
  action: ReturnType<typeof actions.fetchLiveCategorySalesRequest>
) {
  try {
    const response: any = yield call(getLiveCategorySalesApi, action.payload);
    // New shape: { categories, page, size, totalElements, totalPages }.
    // Legacy shape: bare array (or { data: [...] }) with no pagination metadata.
    const categories = Array.isArray(response)
      ? response
      : (response?.categories ?? response?.data ?? []);
    const total = response?.totalElements ?? categories.length;
    const size = response?.size ?? action.payload?.size ?? categories.length;
    yield put(actions.fetchLiveCategorySalesSuccess({
      categories,
      page: response?.page ?? action.payload?.page ?? 1,
      size,
      totalElements: total,
      totalPages: response?.totalPages ?? (size ? Math.max(1, Math.ceil(total / size)) : 1),
    }));
  } catch (error: any) {
    yield put(actions.fetchLiveCategorySalesFailure(error.message || 'Failed to fetch category sales'));
  }
}

function* fetchLiveStaffRefundsSaga(
  action: ReturnType<typeof actions.fetchLiveStaffRefundsRequest>
) {
  try {
    const response: any = yield call(getLiveStaffRefundsApi, action.payload);
    const data = Array.isArray(response) ? response : (response?.data ?? []);
    yield put(actions.fetchLiveStaffRefundsSuccess(data));
  } catch (error: any) {
    yield put(actions.fetchLiveStaffRefundsFailure(error.message || 'Failed to fetch staff refunds'));
  }
}

function* fetchLiveOutstandingValueSaga(action: ReturnType<typeof actions.fetchLiveOutstandingValueRequest>) {
  try {
    const response: any = yield call(getLiveOutstandingValueApi, action.payload);
    yield put(actions.fetchLiveOutstandingValueSuccess(response));
  } catch (error: any) {
    yield put(actions.fetchLiveOutstandingValueFailure(error.message || 'Failed to fetch outstanding value'));
  }
}

function* fetchLabourEmployeeSalesSaga(action: ReturnType<typeof actions.fetchLabourEmployeeSalesRequest>) {
  try {
    const response: any = yield call(getLabourEmployeeSalesApi, action.payload);
    yield put(actions.fetchLabourEmployeeSalesSuccess(response));
  } catch (error: any) {
    yield put(actions.fetchLabourEmployeeSalesFailure(error.message || 'Failed to fetch labour employee sales'));
  }
}

function* fetchLabourTipsSaga(action: ReturnType<typeof actions.fetchLabourTipsRequest>) {
  try {
    const response: any = yield call(getLabourTipsApi, action.payload);
    yield put(actions.fetchLabourTipsSuccess(response));
  } catch (error: any) {
    yield put(actions.fetchLabourTipsFailure(error.message || 'Failed to fetch labour tips'));
  }
}

function* fetchLabourVoidsSaga(action: ReturnType<typeof actions.fetchLabourVoidsRequest>) {
  try {
    const response: any = yield call(getLabourVoidsApi, action.payload);
    yield put(actions.fetchLabourVoidsSuccess(response));
  } catch (error: any) {
    yield put(actions.fetchLabourVoidsFailure(error.message || 'Failed to fetch labour voids'));
  }
}

function* fetchLabourWorkingHoursSaga(action: ReturnType<typeof actions.fetchLabourWorkingHoursRequest>) {
  try {
    const response: any = yield call(getLabourWorkingHoursApi, action.payload);
    yield put(actions.fetchLabourWorkingHoursSuccess(response));
  } catch (error: any) {
    yield put(actions.fetchLabourWorkingHoursFailure(error.message || 'Failed to fetch labour working hours'));
  }
}

function* fetchLabourStaffPerformanceSaga(action: ReturnType<typeof actions.fetchLabourStaffPerformanceRequest>) {
  try {
    const response: any = yield call(getLabourStaffPerformanceApi, action.payload);
    yield put(actions.fetchLabourStaffPerformanceSuccess(response));
  } catch (error: any) {
    yield put(actions.fetchLabourStaffPerformanceFailure(error.message || 'Failed to fetch labour staff performance'));
  }
}

function* fetchLabourWorkingHoursLogSaga(action: ReturnType<typeof actions.fetchLabourWorkingHoursLogRequest>) {
  try {
    const response: any = yield call(getLabourWorkingHoursLogApi, action.payload);
    yield put(actions.fetchLabourWorkingHoursLogSuccess(response));
  } catch (error: any) {
    yield put(actions.fetchLabourWorkingHoursLogFailure(error.message || 'Failed to fetch labour working hours log'));
  }
}

function* fetchLabourKpiComparisonSaga(action: ReturnType<typeof actions.fetchLabourKpiComparisonRequest>) {
  try {
    // ZAR-1661 — same contract as fetchSnapshotComparisonSaga: the comparison window
    // follows the SHAPE of the selected period and is resolved here, not in LaborReport.
    const { startDate, endDate } = action.payload;
    const comparison: { compStartDate?: string; compEndDate?: string } =
      comparisonQueryParams(startDate, endDate);

    const response: any = yield call(getLabourKpiComparisonApi, { ...action.payload, ...comparison });

    // ZAR-1662 — Labor's own source for the truncation label. This endpoint reports the
    // same `currentTime` / `locationTimezone` pair as the snapshot comparison, so the date
    // control on Labor states the store's hour without waiting for a Snapshot visit and
    // without falling back to the viewer's device clock.
    setServerClock(response?.currentTime, response?.locationTimezone);

    yield put(actions.fetchLabourKpiComparisonSuccess(response));
  } catch (error: any) {
    yield put(actions.fetchLabourKpiComparisonFailure(error.message || 'Failed to fetch labour KPI comparison'));
  }
}

function* fetchLabourEmployeesSaga(action: ReturnType<typeof actions.fetchLabourEmployeesRequest>) {
  try {
    const response: any = yield call(getLabourEmployeesApi, action.payload);
    yield put(actions.fetchLabourEmployeesSuccess(response));
  } catch (error: any) {
    yield put(actions.fetchLabourEmployeesFailure(error.message || 'Failed to fetch labour employees'));
  }
}
